import { ErrorsOr, invertErrorsOr, mapErrorsOr } from "@me8eon/errors";
import { validateMap, validateMapLazy, Validator } from "@me8eon/validate";


export type LineToCodec<T> = (lineNumber: number) => Codec<T>;
/**
 * Codec defines functions for encoding values to strings and decoding strings to values,
 * with potential errors encapsulated in an ErrorsOr monad.
 */
export type Codec<T> = {
    /**
     * Encodes a value of type T to a string, potentially returning errors.
     * @param t - The value to encode.
     * @returns An ErrorsOr containing the encoded string or errors.
     */
    encode: (t: T) => ErrorsOr<string>;

    /**
     * Decodes a string to a value of type T, potentially returning errors.
     * @param s - The string to decode.
     * @returns An ErrorsOr containing the decoded value or errors.
     */
    decode: (s: string) => ErrorsOr<T>;
};

/**
 * Encodes an array of values into a newline-separated string using codecs generated per line.
 *
 * @param codec - A function generating a Codec<T> for each line, typically used for providing contextual errors.
 * @param ts - An array of values to encode.
 * @returns An ErrorsOr containing the encoded newline-separated string or errors.
 */
export function encodeLines<T>(codec: LineToCodec<T>, ts: T[]): ErrorsOr<string> {
    return mapErrorsOr(
        invertErrorsOr(ts.map((t, i) => codec(i).encode(t))),
        lines => lines.join("\n"),
    );
}

/**
 * Decodes a newline-separated string into an array of values using codecs generated per line.
 * Empty lines are automatically skipped.
 *
 * @param codec - A function generating a Codec<T> for each line, typically used for providing contextual errors.
 * @param s - A newline-separated string to decode.
 * @returns An ErrorsOr containing the decoded array of values or errors.
 */
export function decodeLines<T>(codec: LineToCodec<T>, s: string): ErrorsOr<T[]> {
    const lines = s.split("\n").filter(line => line.trim() !== "");
    return invertErrorsOr(lines.map((t, i) => codec(i).decode(t)));
}

export function defaultStringCodec<T extends string>(defaultValue: T): Codec<T> {
    return {
        encode: (t) => ({ value: t === null || t === undefined ? defaultValue : t }),
        decode: (t) => ({ value: t === null || t === undefined ? defaultValue : t as T }),
    };
}

export const stringCodec: Codec<string> = {
    encode: (t) => ({ value: t }),
    decode: (s) => ({ value: s }),
};

export type JsonCodecConfig<T> = {
    validate?: Validator<T>;
    encoderCleaner?: (t: T) => T;
    decoderCleaner?: (t: T) => T;
};


export const jsonCodecForLine = <T>(prefix: string): LineToCodec<T> => (lineNumber: number): Codec<T> => {
    return jsonCodec<T>(`${prefix} ${lineNumber}`, {
        validate: () => () => [],
        encoderCleaner: (x) => x,
        decoderCleaner: (x) => x,
    });
};

export function jsonCodec<T>(
    context: string,
    {
        validate = () => () => [],
        encoderCleaner = (x) => x,
        decoderCleaner = (x) => x,
    }: JsonCodecConfig<T> = {},
): Codec<T> {

    return {
        encode: (t) =>
            validateMap<T, string>(`${context} encode`, validate)(
                t,
                (t) => JSON.stringify(encoderCleaner(t)),
            ),

        decode: (s) =>
            validateMapLazy<T, T>(`${context} decode`, validate)(
                () => decoderCleaner(JSON.parse(s)),
                (cleaned) => cleaned,
            ),
    };
}
